import React, { useEffect, useMemo, useRef, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
} from "@mui/material";
import {
  useDeleteSubjectMutation,
  useGetallSubjectQuery,
  useReorderSubjectsMutation,
} from "../../../Api/attoDeskApi";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ISubject } from "../../../Api/Interface/api.interface";
import NewPopUpDepartment from "./subjectChangeName";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import Pagination from "../../../Components/Pagination";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import useIsMobile from "../../../Core/hook/useIsMobile";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const isMobileRes = useIsMobile();
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [selectedCoursing, setSelectedCoursing] = useState<ISubject | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<ISubject | null>(null);

  const { data, isLoading, isError, refetch } = useGetallSubjectQuery({
    searchText: currentSearchQuery,
  });

  useEffect(() => {
    if (debouncedSearchQuery.length >= 1 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDialog = (coursing: ISubject) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: ISubject) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteSubjectMutation();
  const handleDelete = async (id: number) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the subject");
    }
  };

  const coursings: ISubject[] = Array.isArray(data?.data)
    ? (data?.data as ISubject[])
    : [];

  // ── Drag-and-drop ordering ──
  // Local working copy so drags feel instant; synced whenever the query
  // refreshes. Disabled while searching, since a filtered subset can't be
  // meaningfully reordered against the full alphabetical list.
  const [orderedRows, setOrderedRows] = useState<ISubject[]>([]);
  const orderedRowsRef = useRef<ISubject[]>([]);
  const dragIndexRef = useRef<number | null>(null);
  const [reorderSubjects] = useReorderSubjectsMutation();
  const dragEnabled = !currentSearchQuery;

  useEffect(() => {
    setOrderedRows(coursings);
    orderedRowsRef.current = coursings;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDragStart = (absIndex: number) => {
    dragIndexRef.current = absIndex;
  };

  const handleDragOver = (e: React.DragEvent, absIndex: number) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === absIndex) return;
    setOrderedRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(absIndex, 0, moved);
      orderedRowsRef.current = next;
      return next;
    });
    dragIndexRef.current = absIndex;
  };

  const handleDrop = async () => {
    dragIndexRef.current = null;
    try {
      const response = await reorderSubjects({
        ordered_ids: orderedRowsRef.current.map((r) => r.id),
      }).unwrap();
      if (response.status) {
        showMessage(response.message || "Subject order updated");
        refetch();
      } else {
        showErrorMessage(response.message || "Failed to update order");
      }
    } catch {
      showErrorMessage("Failed to update subject order");
    }
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item>
          <Typography className={classes.cardTitle}>Subjects</Typography>
        </Grid>
        <Grid item sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{ display: "flex" }}
          >
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: {
                    root: classes.formControlList,
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name .."
                className={classes.searchTextField}
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader} sx={{ width: 40 }} />
                <TableCell className={classes.tablheader}>
                  <strong>Name</strong>
                </TableCell>
                <TableCell
                  className={classes.tablheader}
                  sx={{ textAlign: "right" }}
                >
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || orderedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                orderedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: ISubject, index) => {
                    const absIndex = page * rowsPerPage + index;
                    return (
                    <TableRow
                      key={row.id}
                      className={classes.tableRowHover}
                      draggable={dragEnabled}
                      onDragStart={() => handleDragStart(absIndex)}
                      onDragOver={(e) => dragEnabled && handleDragOver(e, absIndex)}
                      onDrop={() => dragEnabled && handleDrop()}
                      sx={dragEnabled ? { cursor: "grab" } : undefined}
                    >
                      <TableCell className={classes.tablCell} sx={{ width: 40, px: 1 }}>
                        <DragIndicatorIcon
                          sx={{
                            fontSize: 18,
                            color: dragEnabled ? "text.disabled" : "action.disabledBackground",
                            cursor: dragEnabled ? "grab" : "not-allowed",
                            verticalAlign: "middle",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className={classes.tablCell}
                      >
                        {toProperCase(row.subject_name)}
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Grid
                          container
                          spacing={1}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Grid item>
                            <Button
                              // variant="contained"
                              className={classes.editButtonList}
                              onClick={() => handleOpenDialog(row)}
                            >
                              <EditSharpIcon className={classes.editListIcon} />
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              // variant="contained"
                              className={classes.deleteButtonList}
                              onClick={() => handleOpenDeletePopup(row)}
                            >
                              <DeleteOutlineSharpIcon
                                className={classes.editListIcon}
                              />
                            </Button>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={orderedRows.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer>
      {selectedCoursing && (
          <NewPopUpDepartment
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(Number(coursingToDelete.id));
            }}
            title="Delete Subject"
            content={`Are you sure you want to delete "${coursingToDelete.subject_name}"?`}
          />
        )}
      </Grid>
    </Card>
  );
};

export default Index;
